import { useState, useCallback } from "react";
import { differenceInYears, differenceInMonths } from "date-fns";
import {
  PawPrint,
  Heart,
  Syringe,
  Bug,
  Brain,
  Phone,
  ChevronLeft,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import PetFormHeader from "@/components/pet-form/PetFormHeader";
import DadosBasicos from "@/components/pet-form/DadosBasicos";
import SaudeCuidados from "@/components/pet-form/SaudeCuidados";
import CarteiraVacinas from "@/components/pet-form/CarteiraVacinas";
import ControleParasitarios from "@/components/pet-form/ControleParasitarios";
import Comportamento from "@/components/pet-form/Comportamento";
import Emergencia from "@/components/pet-form/Emergencia";
import ImageCropModal from "@/components/ImageCropModal";

export interface PetFormData {
  nome: string;
  tipo_pet: "cachorro" | "gato" | "outro" | "";
  outro_tipo_pet: string;
  raca: string;
  sexo_pet: "Macho" | "Fêmea" | "";
  data_nascimento: string;
  peso: string;
  idade: string;
  idade_meses: string;

  microchipado: boolean;
  microchip_codigo: string;
  castrado: boolean;

  observacoes: string;

  toma_medicacao: boolean;
  medicacao: string;
  obs_medicacoes: string;

  possui_deficiencia: boolean;
  tipo_deficiencia: string;

  tem_alergia: boolean;
  alergias: string;

  alimentacao_especial: boolean;
  descricao_alimentacao: string;

  doencas: string;

  sociavel_pets: boolean;
  sociavel_criancas: boolean;
  agressivo: boolean;
  medo_chuva: boolean;
  medo_trovao_fogos: boolean;
  dorme_dentro: boolean;
  aceita_outros_pets: boolean;
  ja_ficou_hospedado: boolean;
  precisa_espaco_cercado: boolean;
  precisa_supervisao_constante: boolean;

  horario_refeicao: string;
  horario_passeio: string;
  objetos_essenciais: string;

  emergencia: string;
  contato_medico_vet: string;
  autoriza_vet: boolean;
  autoriza_plantao: boolean;

  vacinas: VacinaEntry[];
  parasitas: ParasitarioEntry[];
}

export type VacinaEntry = {
  id?: string | number;
  vacina_id?: number | null;
  vacina_nome_custom?: string | null;
  nome?: string;
  dose_num?: number | null;
  data_aplicacao?: string | null;
  data_revacinar?: string | null;
  fabricante?: string | null;
  lote?: string | null;
  via?: string | null;
  peso?: string | number | null;
  veterinario?: string | null;
  nome_veterinario?: string | null;
  validado?: boolean | number;
  _delete?: number;
};

export interface ParasitarioEntry {
  id: string;
  tipo: "antipulgas" | "vermifugo" | "";
  produto: string;
  data_aplicacao: string;
  data_reaplicar: string;
  peso?: string;
  dose?: string;
  observacoes?: string;
  validado?: number | boolean;
}

const initialFormData: PetFormData = {
  nome: "",
  tipo_pet: "",
  outro_tipo_pet: "",
  raca: "",
  sexo_pet: "",
  data_nascimento: "",
  peso: "",
  idade: "",
  idade_meses: "",

  microchipado: false,
  microchip_codigo: "",
  castrado: false,

  observacoes: "",

  toma_medicacao: false,
  medicacao: "",
  obs_medicacoes: "",

  possui_deficiencia: false,
  tipo_deficiencia: "",

  tem_alergia: false,
  alergias: "",

  alimentacao_especial: false,
  descricao_alimentacao: "",

  doencas: "",

  sociavel_pets: false,
  sociavel_criancas: false,
  agressivo: false,
  medo_chuva: false,
  medo_trovao_fogos: false,
  dorme_dentro: false,
  aceita_outros_pets: true,
  ja_ficou_hospedado: false,
  precisa_espaco_cercado: false,
  precisa_supervisao_constante: false,

  horario_refeicao: "",
  horario_passeio: "",
  objetos_essenciais: "",

  emergencia: "",
  contato_medico_vet: "",
  autoriza_vet: false,
  autoriza_plantao: false,

  vacinas: [],
  parasitas: [],
};

export function calcularIdade(dataNascimento: string): string {
  if (!dataNascimento) return "";

  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  const anos = differenceInYears(hoje, nascimento);
  const meses = differenceInMonths(hoje, nascimento) % 12;

  if (anos > 0) {
    return `${anos} ano${anos > 1 ? "s" : ""}${
      meses > 0 ? ` e ${meses} ${meses > 1 ? "meses" : "mês"}` : ""
    }`;
  }

  if (meses > 0) {
    return `${meses} ${meses > 1 ? "meses" : "mês"}`;
  }

  return "Recém-nascido";
}

const CadastrarPet = () => {
  const [formData, setFormData] = useState<PetFormData>(initialFormData);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modoBasico = searchParams.get("modo") === "basico";
  const redirectParam = searchParams.get("redirect");
  const redirectAfterSave =
    redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
      ? redirectParam
      : modoBasico
        ? "/tutor/dashboard"
        : "/tutor/pets";

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [fotoFile, setFotoFile] = useState<File | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const updateField = useCallback(
    <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        setCropImageSrc(reader.result);
        setShowCropModal(true);
      }
    };

    reader.readAsDataURL(file);
  }

  function handleCropConfirm(file: File) {
    setFotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setShowCropModal(false);
    setCropImageSrc(null);
  }

  function handleCropCancel() {
    setShowCropModal(false);
    setCropImageSrc(null);
  }

  async function salvarVacinas(petId: number | string) {
    const token = (
      document.querySelector(
        'meta[name="csrf-token"]'
      ) as HTMLMetaElement | null
    )?.content;

    for (const vacina of formData.vacinas) {
      const temNomeCustom =
        vacina.vacina_id == null &&
        typeof vacina.vacina_nome_custom === "string" &&
        vacina.vacina_nome_custom.trim() !== "";

      if ((!vacina.vacina_id && !temNomeCustom) || !vacina.data_aplicacao) {
        continue;
      }

      const response = await fetch(`/pets/${petId}/vacinas/salvar`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          vacina_id: vacina.vacina_id ?? null,
          vacina_nome_custom: temNomeCustom
            ? vacina.vacina_nome_custom?.trim()
            : null,
          dose_num: vacina.dose_num || 1,
          data_aplicacao: vacina.data_aplicacao,
          data_revacinar: vacina.data_revacinar || null,
          fabricante: vacina.fabricante || "",
          lote: vacina.lote || "",
          via: vacina.via || "",
          veterinario: vacina.nome_veterinario || vacina.veterinario || "",
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Erro ao salvar vacinas.");
      }
    }
  }

  async function salvarParasitarios(petId: number | string) {
    const token = (
      document.querySelector(
        'meta[name="csrf-token"]'
      ) as HTMLMetaElement | null
    )?.content;

    for (const parasita of formData.parasitas) {
      if (!parasita.tipo || !parasita.data_aplicacao) continue;

      const response = await fetch(`/pets/${petId}/parasitas/salvar`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          tipo: parasita.tipo,
          produto: parasita.produto || "",
          data_aplicacao: parasita.data_aplicacao,
          data_reaplicar: parasita.data_reaplicar || null,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Erro ao salvar parasitários.");
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome || !formData.tipo_pet) {
      toast.error("Preencha os campos obrigatórios: Nome e Tipo do Pet");
      return;
    }

    try {
      setSaving(true);

      const payload = new FormData();

      if (modoBasico) {
        payload.append("modo", "basico");
      }

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "vacinas" || key === "parasitas") return;
        if (value === null || value === undefined) return;

        if (typeof value === "boolean") {
          payload.append(key, value ? "1" : "0");
          return;
        }

        payload.append(key, String(value));
      });

      if (fotoFile) {
        payload.append("foto", fotoFile);
      }

      const token = (
        document.querySelector(
          'meta[name="csrf-token"]'
        ) as HTMLMetaElement | null
      )?.content;

      const response = await fetch(`/react-api/pets`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: payload,
      });

      let data: any = null;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text };
      }

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao cadastrar pet.");
      }

      const petIdSalvo = data?.pet?.id || data?.id;

      if (!petIdSalvo) {
        throw new Error("Pet criado, mas o ID não foi retornado.");
      }

      if (!modoBasico) {
        await salvarVacinas(petIdSalvo);
        await salvarParasitarios(petIdSalvo);
      }

      toast.success(modoBasico ? "Dados básicos do pet salvos." : "Pet cadastrado com sucesso.");
      window.location.href = redirectAfterSave;
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Erro ao cadastrar pet.");
    } finally {
      setSaving(false);
    }
  };

  const idade = calcularIdade(formData.data_nascimento);

  const sections = [
    { icon: PawPrint, title: "Dados Básicos" },
    { icon: Heart, title: "Saúde & Cuidados" },
    { icon: Syringe, title: "Carteira de Vacinas" },
    { icon: Bug, title: "Controle de Parasitários" },
    { icon: Brain, title: "Comportamento" },
    { icon: Phone, title: "Emergência" },
  ];

  return (
    <div className="min-h-screen gradient-hero">
      <div className="container max-w-4xl py-6 px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 animate-fade-in-up">
          <button
            onClick={() => navigate("/")}
            className="hover:text-forest transition-smooth"
          >
            Início
          </button>
          <span>/</span>
          <button
            onClick={() => {
                window.location.href = modoBasico ? "/tutor/dashboard" : redirectAfterSave;
              }}
            className="hover:text-forest transition-smooth"
          >
            {modoBasico ? "Ativação" : "Meus Pets"}
          </button>
          <span>/</span>
          <span className="text-forest font-semibold">
            {modoBasico ? "Dados Básicos" : "Cadastrar Pet"}
          </span>
        </nav>

        <PetFormHeader
          photoPreview={photoPreview}
          handlePhotoChange={handlePhotoChange}
          petName={formData.nome}
          isEditing={false}
        />

        {modoBasico && (
          <div className="mb-6 rounded-xl border border-forest/20 bg-forest/5 p-4 text-sm text-muted-foreground">
            <strong className="text-forest">Cadastro básico:</strong>{" "}
            preencha somente os dados essenciais agora. Saúde, vacinas, rotina e emergência
            ficam para a etapa de segurança antes da reserva.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <section
            className="animate-fade-in-up stagger-1 opacity-0"
            style={{ animationFillMode: "forwards" }}
          >
            <SectionCard icon={sections[0].icon} title={sections[0].title}>
              <DadosBasicos
                formData={formData}
                updateField={updateField}
                idade={idade}
                photoPreview={photoPreview}
                handlePhotoChange={handlePhotoChange}
              />
            </SectionCard>
          </section>

          {!modoBasico && (
            <>
              <section
                className="animate-fade-in-up stagger-2 opacity-0"
                style={{ animationFillMode: "forwards" }}
              >
                <SectionCard icon={sections[1].icon} title={sections[1].title}>
                  <SaudeCuidados formData={formData} updateField={updateField} />
                </SectionCard>
              </section>

              <section
                className="animate-fade-in-up stagger-3 opacity-0"
                style={{ animationFillMode: "forwards" }}
              >
                <SectionCard icon={sections[2].icon} title={sections[2].title}>
                  <CarteiraVacinas formData={formData} updateField={updateField} />
                </SectionCard>
              </section>

              <section
                className="animate-fade-in-up stagger-4 opacity-0"
                style={{ animationFillMode: "forwards" }}
              >
                <SectionCard icon={sections[3].icon} title={sections[3].title}>
                  <ControleParasitarios
                    formData={formData}
                    updateField={updateField}
                  />
                </SectionCard>
              </section>

              <section
                className="animate-fade-in-up stagger-5 opacity-0"
                style={{ animationFillMode: "forwards" }}
              >
                <SectionCard icon={sections[4].icon} title={sections[4].title}>
                  <Comportamento formData={formData} updateField={updateField} />
                </SectionCard>
              </section>

              <section
                className="animate-fade-in-up stagger-6 opacity-0"
                style={{ animationFillMode: "forwards" }}
              >
                <SectionCard icon={sections[5].icon} title={sections[5].title}>
                  <Emergencia formData={formData} updateField={updateField} />
                </SectionCard>
              </section>
            </>
          )}

          <div
            className="flex justify-end gap-3 pt-4 pb-8 animate-fade-in-up stagger-7 opacity-0"
            style={{ animationFillMode: "forwards" }}
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(modoBasico ? "/tutor/dashboard" : redirectAfterSave)}
              className="gap-2"
              disabled={saving}
            >
              <ChevronLeft className="w-4 h-4" />
              Cancelar
            </Button>

            <Button
              type="submit"
              className="gradient-primary gap-2 text-primary-foreground hover:opacity-90 transition-smooth"
              disabled={saving}
            >
              <Save className="w-4 h-4" />
              {saving ? "Salvando..." : modoBasico ? "Salvar e encontrar anfitriões" : "Salvar Pet"}
            </Button>
          </div>
        </form>
      </div>

      {showCropModal && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          aspect={1}
          outputWidth={800}
          outputHeight={800}
          title="Ajustar foto do pet"
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
};

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card rounded-xl card-shadow overflow-hidden">
      <div className="gradient-primary px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-foreground/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary-foreground" />
        </div>
        <h2 className="text-lg font-bold text-primary-foreground">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default CadastrarPet;
